/**
 * The badges an admin grants a member (admin site → Community → Members) and
 * the **✓ verified** mark the member can apply for (Community → Verification,
 * the conditions are created by the admin):
 *
 *   · the blue ✓  — rendered with the brand picture
 *     `public/verify/verify_check.png` so the storefront, the dashboard and the
 *     printed receipts all show the same mark;
 *   · the VIP member award icon (green VIP / gold / platinum).
 *
 * Pure icons — hover shows what they mean, and a normal member renders nothing,
 * so it can be dropped next to any name. The API includes `verified` +
 * `vip_tier` in every user payload (`CommunityUser.to_dict`), so posts,
 * comments, profiles and the leaderboard all get the badges for free.
 */
import React from 'react';
import { FiAward } from 'react-icons/fi';
import { useLanguage } from '../../i18n';

const VIP_ICON = {
  vip: { title: 'vipTier', cls: 'text-emerald-500' },
  gold: { title: 'vipGold', cls: 'text-amber-500' },
  platinum: { title: 'vipPlatinum', cls: 'text-slate-500' },
};

/** The ✓ picture (public/verify/verify_check.png) — one mark everywhere. */
export function VerifyCheck({ size = 'sm', className = '' }) {
  const { t } = useLanguage();
  const dim = size === 'lg' ? 'w-4 h-4' : 'w-3.5 h-3.5';
  return (
    <img
      src={`${process.env.PUBLIC_URL}/verify/verify_check.png`}
      alt={t('verifiedBadge')}
      title={t('verifiedBadge')}
      className={`${dim} inline-block shrink-0 object-contain ${className}`}
    />
  );
}

export default function UserBadges({ user, size = 'sm', className = '' }) {
  const { t } = useLanguage();
  const verified = !!user?.verified;
  const vip = VIP_ICON[String(user?.vip_tier || '').toLowerCase()];
  if (!verified && !vip) return null;

  const dim = size === 'lg' ? 'w-4 h-4' : 'w-3.5 h-3.5';
  return (
    <span className={`inline-flex items-center gap-1 align-middle ${className}`}>
      {verified && (
        <span className="inline-flex items-center shrink-0">
          <VerifyCheck size={size} />
        </span>
      )}
      {vip && (
        <span title={t(vip.title)} className={`inline-flex items-center shrink-0 ${vip.cls}`}>
          <FiAward className={dim} strokeWidth={2.5} />
        </span>
      )}
    </span>
  );
}

